import { Observer, useLocalStore } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Button, Grid } from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { Select, Text, Switch } from "@components/formx";
import { ErrorBoundary } from "@components/error-boundary";
import { ContractsPriceTemplateType, PriceListWorkType } from "@graphql";
import { Add } from "@material-ui/icons";
import { store } from "@store";
import { toJS } from "mobx";
import { ValidateCustomerContractOptionResult } from "@api/timereports";
import { ContractPriceTemplateRecordInput } from "../ContractsPriceTemplateFormInput";
import { PriceRecordAltStore, recordType } from "./PriceRecordAltStore";
import { RecordsByWorkType } from "./RecordsByWorkType";
import { WorkTypesAddDialog } from "./WorkTypesAddDialog";
import { DayTypesAddDialog } from "./DayTypesAddDialog";
import { ContractVariant, getIsConultantContract } from "../utils";

type Props = {
  formStore: any;
  name: string;
  // addRecordByType: (item: ContractPriceTemplateRecordInput) => void;
  duplicateRecord: (item: ContractPriceTemplateRecordInput) => void;
  variant?: ContractVariant;
  type?: ContractsPriceTemplateType;
  validationResult?: ValidateCustomerContractOptionResult[];
  disabled?: boolean;
  isNationalProcurementPriceStrategy?: boolean;
  showArticleNumberForOtherSpeciality?: boolean;
  isBonlivaCare?: boolean;
};

export function PriceRecordFromAlt({
  formStore,
  variant,
  type,
  name,
  disabled,
  duplicateRecord,
  validationResult,
  showArticleNumberForOtherSpeciality = false,
  isNationalProcurementPriceStrategy,
  isBonlivaCare = false
}: Props) {
  const hasPermission = store().user.can("contracts-price-template:create:all");

  const isCustomArr = toJS(formStore.getValue(name)).map((x) => !!x.customPrice);
  const localStore = useLocalStore(() => new PriceRecordAltStore(formStore, name));
  const [isCustom, setCustom] = useState(isCustomArr);
  const recordsRequired = formStore.getValue(name)?.length === 0;

  useEffect(() => {
    setCustom(toJS(formStore.getValue(name)).map((x) => !!x.customPrice));
    localStore.resetSortRecords(formStore.getValue(name));
  }, [formStore.getValue(name)]);

  const handleShowCustomInput = (e, i, customPriceName) => {
    const res = isCustom.map((x, ind) => (ind === i ? !x : x));
    if (!e.target.checked) {
      formStore.setValue(customPriceName, null);
    }
    setCustom(res);
  };

  const handleDayTypes = (value: string[]) => {
    setCustom([...isCustom, ...value.map(() => false)]);
    const newRecords = localStore.handleDayTypes(value) ?? [];
    formStore.push(name, newRecords);
  };

  const handleDuplicate = (record: recordType) => {
    const { name, index, ...item } = record;
    setCustom([...isCustom, !!record.customPrice]);
    duplicateRecord && duplicateRecord(item);
    // localStore.initSortRecords();
  };

  return (
    <Observer>
      {() => (
        <ErrorBoundary>
          <Grid
            container
            spacing={1}
            style={{
              position: "relative",
              minHeight: "45px"
            }}
          >
            <Button
              style={{
                position: "absolute",
                right: 6,
                top: 6,
                border: recordsRequired ? "2px solid #F65629" : "initial"
              }}
              color={"secondary"}
              variant={"contained"}
              aria-label="add"
              onClick={localStore.toggleWorkTypeDialog}
            >
              Arbetstyp
              <Add fontSize="small" />
            </Button>
            {variant === "template" && (
              <>
                <Grid item xs={6}>
                  <Text label={`${locale.Title}`} name={"name"} key={"title"} />
                </Grid>
                <Grid item xs={3}>
                  <Select
                    label={locale.ContractType}
                    disabled={!hasPermission}
                    name={`type`}
                    options={Object.values(ContractsPriceTemplateType).toLabelValue(
                      (v) => locale.contractsPrice.priceTemplateType[v]
                    )}
                  />
                </Grid>

                <Grid item xs={1}>
                  <Grid item xs={12} sm={3}>
                    <Switch
                      disabled={!hasPermission}
                      name="private"
                      label="Privat mall"
                    />
                  </Grid>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              {Object.values(PriceListWorkType).map((workType) => (
                <RecordsByWorkType
                  key={workType}
                  records={localStore.records}
                  items={localStore.sortedRecordIds[workType]}
                  workType={workType}
                  activeWorkTypes={localStore.allUniqWorkTypeFromRecords}
                  toggleDayTypeDialog={localStore.toggleDayTypeDialog}
                  handleSortRecords={localStore.handleSortRecords}
                  formStore={formStore}
                  disabled={disabled}
                  isCustom={isCustom}
                  handleDuplicate={handleDuplicate}
                  handleDelete={localStore.handleDelete}
                  handleShowCustomInput={handleShowCustomInput}
                  type={type}
                  validationResult={validationResult}
                  isNationalProcurementPriceStrategy={isNationalProcurementPriceStrategy}
                  showArticleNumberForOtherSpeciality={
                    showArticleNumberForOtherSpeciality
                  }
                />
              ))}
            </Grid>
          </Grid>
          <WorkTypesAddDialog
            isConsultantContract={getIsConultantContract({ type, variant })}
            values={localStore.allUniqWorkTypeFromRecords}
            open={localStore.showWorkTypeDialog}
            onClose={localStore.toggleWorkTypeDialog}
            onSubmit={localStore.handleWorkTypes}
            showPsychologistOpt={localStore.isPsychologist}
            showDoctorOpt={localStore.isDoctor}
            isBonlivaCare={isBonlivaCare}
          />
          <DayTypesAddDialog
            open={localStore.showDayTypeDialog}
            onClose={localStore.toggleDayTypeDialog}
            onSubmit={handleDayTypes}
          />
        </ErrorBoundary>
      )}
    </Observer>
  );
}
